/**
 * The CRM's own colours, light and dark.
 *
 * Carried across verbatim from the OpenSilver app's `ThemeService.Palette`, which is what makes the
 * two look like one product during the coexistence period rather than two applications that happen
 * to share a server. The names are that table's names with the `Brush` suffix dropped.
 *
 * Every value is measured for WCAG AA against the surface it sits on in both themes - the status
 * colours especially, which are each paired with their own tint. A test asserts that, so a value
 * changed here without checking it fails rather than shipping a chip nobody can read.
 *
 * Kept as hex STRINGS rather than Webwin `Color` objects on purpose: this file is the transcription
 * of the C# table, and a reviewer has to be able to diff the two by eye. `crmPalette()` converts.
 */
import { hex, type Color } from 'webwin';

/** Every named colour the CRM draws with. One entry per brush in the OpenSilver App.xaml. */
export interface CrmColors {
  /** Brand blue. A SURFACE colour - it always has white on it - so it does not change with theme. */
  brand: Color;
  brandDark: Color;
  /** The navigation rail, which is dark in both themes. */
  sidebar: Color;
  sidebarText: Color;
  sidebarMuted: Color;
  /** Behind the page content. */
  pageBackground: Color;
  /** A card: the surface almost everything sits on. */
  card: Color;
  /** A second surface inside a card, for a row band or a nested well. */
  altSurface: Color;
  border: Color;
  text: Color;
  subtleText: Color;
  mutedText: Color;
  danger: Color;
  dangerBg: Color;
  dangerBorder: Color;
  warn: Color;
  warnBg: Color;
  warnBorder: Color;
  ok: Color;
  okBg: Color;
  info: Color;
  infoBg: Color;
  /** The presence dot on an online device. */
  online: Color;
}

/** Light and dark hex for each colour: exactly the shape of the C# table this came from. */
const TABLE: Record<keyof CrmColors, readonly [light: string, dark: string]> = {
  // Brand is a surface colour in both themes - it always has white on top - so it does not change.
  brand: ['#1F6FEB', '#1F6FEB'],
  brandDark: ['#1A5CC4', '#1A5CC4'],

  // The sidebar is already dark in the light theme; in dark it goes a shade deeper so it still
  // reads as a distinct band rather than merging into the page.
  sidebar: ['#0E1726', '#0B111C'],
  sidebarText: ['#C7D0DE', '#C7D0DE'],
  sidebarMuted: ['#8A94A6', '#8A94A6'],

  pageBackground: ['#F4F6FB', '#0F1216'],
  card: ['#FFFFFF', '#171B21'],
  altSurface: ['#F4F7FB', '#1E242C'],
  border: ['#E2E6EF', '#2B323B'],

  text: ['#1F2430', '#E6EDF3'],
  subtleText: ['#44506A', '#B8C4D0'],
  mutedText: ['#6B7280', '#9AA7B4'],

  // Status colours are measured against their own tint for WCAG AA in both themes.
  danger: ['#B3251C', '#F2726F'],
  dangerBg: ['#FDECEA', '#3E2B2F'],
  dangerBorder: ['#E0B4B4', '#5C3A3C'],
  warn: ['#8A5A00', '#E0A144'],
  warnBg: ['#FFF3E0', '#3B3327'],
  warnBorder: ['#E0B252', '#5E4B2A'],
  ok: ['#1E7B45', '#52C07D'],
  okBg: ['#E6F4EA', '#223932'],
  info: ['#1A5CC4', '#6FA5F7'],
  infoBg: ['#E8ECF7', '#203047'],
  online: ['#2EA043', '#52C07D']
};

/** White, for text on the brand fill. Not in the table because it is not a theme decision. */
export const ON_BRAND = hex('#FFFFFF');

/** The palette for one variant, as Webwin colours. */
export function crmColors(dark: boolean): CrmColors {
  const out = {} as Record<keyof CrmColors, Color>;
  for (const key of Object.keys(TABLE) as (keyof CrmColors)[]) out[key] = hex(TABLE[key][dark ? 1 : 0]);
  return out as CrmColors;
}

/** The raw table, for the contrast test - which needs the strings, not the parsed colours. */
export const CRM_COLOR_TABLE = TABLE;
